import {
	createContext,
	useCallback,
	useContext,
	useMemo,
	useState,
	type ReactNode,
} from "react";

/**
 * Search text that survives navigating away from a table and back.
 * Kept in memory only because search text may contain PHI. It is lost on page
 * reload or app restart.
 */
export function useSearchState(
	searchKey: string,
): [string, (value: string) => void] {
	const context = useContext(SearchStateContext);
	if (!context) {
		throw new Error("useSearchState must be used within a SearchStateProvider");
	}

	const { searches, setSearch: setSearchForKey } = context;
	const search = searches[searchKey] ?? "";
	const setSearch = useCallback(
		(value: string) => setSearchForKey(searchKey, value),
		[setSearchForKey, searchKey],
	);
	return [search, setSearch];
}

export function SearchStateProvider({ children }: { children: ReactNode }) {
	const [searches, setSearches] = useState<Record<string, string>>({});

	const setSearch = useCallback(
		(searchKey: string, value: string) =>
			setSearches((previous) => ({ ...previous, [searchKey]: value })),
		[],
	);

	const contextValue = useMemo(
		() => ({ searches, setSearch }),
		[searches, setSearch],
	);

	return (
		<SearchStateContext.Provider value={contextValue}>
			{children}
		</SearchStateContext.Provider>
	);
}

interface SearchStateContextType {
	searches: Record<string, string>;
	setSearch: (searchKey: string, value: string) => void;
}

const SearchStateContext = createContext<SearchStateContextType | null>(null);
